/**
 * The status tone registry (TMMPDM-0065, TMM-B2). One registry maps every status the console shows
 * to one tone; `StatusChip` is the only component that renders it, so the same status has the same
 * tone on every screen and in every TMM app. Colour is never the only cue (STATE-002): the chip
 * always carries the status in text too.
 *
 * | Tone    | Meaning                                 |
 * | ------- | --------------------------------------- |
 * | success | done, or in good standing               |
 * | info    | pending, or waiting on the system       |
 * | warning | needs someone's action                  |
 * | error   | failed or blocked                       |
 * | neutral | ended, inactive or closed               |
 * | draft   | outlined: not yet in effect             |
 *
 * The shared statuses are TMM profile 1.1 §5 (tmm-app `docs/design/TMM-front-end-profile.md`),
 * copied here as a registry, not as prose. The console's own statuses follow §5's rules and are
 * listed in the TMMPDM-0065 delivery as §5 change requests where another TMM app shows them too.
 * Nothing here composes a status from two server fields: a key is one status the server sent.
 *
 * One label, one tone (1.1): the same visible label never appears in two tones, so a status whose
 * meaning differs by domain takes a different label there. `REVOKED` is split: access withdrawn by a
 * third party is error ("Access revoked"); access ended by its owner is neutral ("Removed"), keyed
 * below by domain. The catalogs hold each label; `src/status/status.guard.test.ts` enforces the rule.
 */

export type Tone = 'success' | 'info' | 'warning' | 'error' | 'neutral' | 'draft';

/** TMM profile 1.1 §5, as written. */
const SHARED_STATUS_TONE = {
  ACTIVE: 'success',
  POSTED: 'success',
  COMMITTED: 'success',
  PAID: 'success',
  DONE: 'success',
  MET: 'success',
  ANSWERED: 'success',
  AGREED: 'success',

  PENDING: 'info',
  QUEUED: 'info',
  PROCESSING: 'info',
  PREVIEWED: 'info',
  IN_PROGRESS: 'info',
  SCHEDULED: 'info',
  PLANNED: 'info',
  ENDING: 'info',

  LOGIN_REQUIRED: 'warning',
  PENDING_EXPIRATION: 'warning',
  PAST_DUE: 'warning',
  ACTION_REQUIRED: 'warning',
  NEEDS_REVIEW: 'warning',
  ATTENTION: 'warning',
  MISSED: 'warning',
  OPEN: 'warning',

  FAILED: 'error',
  DECLINED: 'error',
  REJECTED: 'error',
  REVOKED: 'error',
  BLOCKED: 'error',

  PAUSED: 'neutral',
  CANCELED: 'neutral',
  CANCELLED: 'neutral',
  ENDED: 'neutral',
  CLOSED: 'neutral',
  ARCHIVED: 'neutral',
  REMOVED: 'neutral',
  VOID: 'neutral',
  DROPPED: 'neutral',
  RETIRED: 'neutral',
  SKIPPED: 'neutral',
  INACTIVE: 'neutral',
  SUPERSEDED: 'neutral',
  WAIVED: 'neutral',
  NONE: 'neutral',

  DRAFT: 'draft',
} as const satisfies Record<string, Tone>;

/**
 * This console's own statuses, by the same rules. `CHARGED` is §5's "follows its charge": the
 * console shows the charge's outcome through its own chip beside it, so the payout-status chip
 * itself is neutral rather than a verdict composed from two fields.
 */
const CONSOLE_STATUS_TONE = {
  // Reseller standing (minimum commitment).
  GOOD: 'success',
  PROBATION: 'warning',
  DEACTIVATION_DUE: 'warning',
  DEACTIVATED: 'neutral',
  // Accounts, members, invitations.
  SUSPENDED: 'error',
  DISABLED: 'neutral',
  INVITED: 'info',
  ACCEPTED: 'success',
  EXPIRED: 'neutral',
  // Charges, notices, transfers, subscriptions, deletions.
  SUCCEEDED: 'success',
  REQUESTED: 'info',
  REQUIRES_ACTION: 'warning',
  SENT: 'success',
  SENDING: 'info',
  COMPLETED: 'success',
  ERASING: 'info',
  ERASED: 'success',
  // Add-ons, referrals, seats, settlements.
  OFFERED: 'info',
  LAPSED: 'error',
  WITHDRAWN: 'neutral',
  NETTING: 'info',
  NETTED: 'success',
  REFUNDED: 'neutral',
  VOIDED: 'neutral',
  // An affiliate who applied waits on the reseller's approval: pending, so info (TMMPDM-0077).
  APPLIED: 'info',
  RETAINED: 'neutral',
  CARRIED: 'neutral',
  CHARGED: 'neutral',
  COLLECTED: 'success',
  NOT_REQUIRED: 'neutral',
  // A partner's tax-ID status at pay-api (TMMPDM-0094): verified is in good standing; a mismatch needs
  // the partner's action. Its PENDING (info) and NOT_REQUIRED (neutral) are the registry's own.
  VERIFIED: 'success',
  MISMATCH: 'warning',
  // Subscriptions (TMMPDM-0075): a trial runs to its end, so it is scheduled; a comped subscription is
  // in good standing, free. Both are §5 change requests for tmm-app, which shares these statuses.
  TRIALING: 'info',
  COMPED: 'success',
  // Parked payment events (TMMPDM-0071): re-applied is done; dismissed is closed without applying.
  REAPPLIED: 'success',
  DISMISSED: 'neutral',
  // Customer rate card versions (TMMPDM-0086). In effect, so success. DRAFT stays outlined;
  // RETIRED is the shared neutral status. Earlier published versions stay PUBLISHED.
  PUBLISHED: 'success',
  // Partner ledger statements (TMMPDM-0095, pay-api's settlement statement). Settled ones are done
  // (PAID, COLLECTED above; PAID_EXTERNALLY, SETTLED); payable, paying and collecting wait on the
  // system; a held or negative statement needs someone (a hold released, a balance collected or
  // adjusted); a failed payout failed. CARRIED (under the minimum) stays the registry's neutral.
  HELD: 'warning',
  NEGATIVE: 'warning',
  PAYABLE: 'info',
  PAYING: 'info',
  COLLECTING: 'info',
  PAYOUT_FAILED: 'error',
  PAID_EXTERNALLY: 'success',
  SETTLED: 'success',
  // Partner ledger hold kinds (TMMPDM-0095): each blocks payouts until someone acts, so warning; a
  // returned payout is a failed one. A collection conflict (tmm-api 0162 at c95c4df4, PAYAPI-0163 A3)
  // waits on operations to resolve it, so warning, as REVIEW. A kind the console doesn't know is
  // `ledgerHold.OTHER` below.
  FOR_CAUSE: 'warning',
  REVIEW: 'warning',
  TAX_ID_UNVERIFIED: 'warning',
  PAYOUT_RETURNED: 'error',
  COLLECTION_CONFLICT: 'warning',
  // Tickets (TMMPDM-0109; TMMAPI-0181 item 28). Both types share the registry's IN_PROGRESS (info,
  // "In progress"), and use its OPEN (warning: staff act on it), PLANNED (info) and CLOSED (neutral).
  // A feature request waiting on review is pending; shipped is done; not planned or a duplicate is
  // closed without one. A support case waiting on the member needs someone's action (the member's),
  // so warning, as tmm-app shows it; here its label names the member. Resolved is done. §5 change
  // requests for tmm-app, which shows these statuses too.
  SUBMITTED: 'info',
  UNDER_REVIEW: 'info',
  SHIPPED: 'success',
  NOT_PLANNED: 'neutral',
  DUPLICATE: 'neutral',
  WAITING_ON_YOU: 'warning',
  RESOLVED: 'success',
  // Announcement kinds (TMMPDM-0110; TMMAPI-0181 item 32a), drawn as chips: news is plain; a new
  // feature is good news; maintenance is the system's own work, like a pending job; an important
  // notice asks the reader to act. An announcement's states reuse the registry: DRAFT (draft),
  // SCHEDULED (info), PUBLISHED (success) and EXPIRED (neutral).
  NEWS: 'neutral',
  FEATURE: 'success',
  MAINTENANCE: 'info',
  IMPORTANT: 'warning',
} as const satisfies Record<string, Tone>;

/**
 * A status whose tone depends on what it means in one domain: key `domain.STATUS`. `REVOKED` is §5's
 * error where a third party withdrew access; an invitation is withdrawn by the person who sent it
 * (an owner ended it), so there it is neutral and its label is "Removed".
 */
const DOMAIN_STATUS_TONE = {
  'invitation.REVOKED': 'neutral',
  // A reseller's card that has expired needs the reseller to act; the registry's EXPIRED (an
  // invitation, a code) is neutral. The card's label is its own ("Card expired"): one label, one tone.
  'card.EXPIRED': 'warning',
  // A ledger hold of a kind pay-api added after this console (TMMPDM-0095): still a hold on payouts.
  'ledgerHold.OTHER': 'warning',
} as const satisfies Record<string, Tone>;

export type StatusDomain = 'invitation' | 'card' | 'ledgerHold';

export const STATUS_TONE: Readonly<Record<string, Tone>> = {
  ...SHARED_STATUS_TONE,
  ...CONSOLE_STATUS_TONE,
};

/** The domain-keyed tones, for the one-label-one-tone guard. */
export const DOMAIN_TONES: Readonly<Record<string, Tone>> = DOMAIN_STATUS_TONE;

/** The §5 shared statuses, for the conformance test. */
export const SHARED_STATUSES: Readonly<Record<string, Tone>> = SHARED_STATUS_TONE;

/**
 * The tone of one status the server sent, or `undefined` for a status the registry does not know
 * (it renders as a neutral chip rather than throwing, so a new server status never breaks a
 * list). The match ignores case: a wire value like `requires_action` is the same status. A `domain`
 * picks the tone that status has in that domain, when it has one of its own (`invitation.REVOKED`, `card.EXPIRED`).
 */
export function statusTone(status: string, domain?: StatusDomain): Tone | undefined {
  const key = status.toUpperCase();
  if (domain !== undefined) {
    const scoped = `${domain}.${key}`;
    if (Object.hasOwn(DOMAIN_STATUS_TONE, scoped))
      return DOMAIN_STATUS_TONE[scoped as keyof typeof DOMAIN_STATUS_TONE];
  }
  return Object.hasOwn(STATUS_TONE, key) ? STATUS_TONE[key] : undefined;
}
